import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth'
import { api } from '../api'
import { useI18n } from '../i18n'
import { useDisplayMode } from '../displayMode'
import SimpleHome from '../components/SimpleHome'
import type { Booking, DaySummary } from '../types'
import { addDays, formatHour, formatLong, parseYmd, todayYmd, ymd } from '../lib/dates'
import { RAG_META } from '../lib/rag'
import { CoverageBar, Empty, RagDot, Spinner } from '../components/ui'
import { IconCalendar, IconInbox } from '../components/icons'

export default function Dashboard() {
  const { user, hasRole } = useAuth()
  const { t } = useI18n()
  const { mode } = useDisplayMode()
  // Members in large/simple mode get a stripped-down home; supervisors keep the
  // full dashboard (just enlarged) so management stays reachable.
  const simple = mode === 'simple' && !hasRole('supervisor')
  const [days, setDays] = useState<DaySummary[]>([])
  const [mine, setMine] = useState<Booking[]>([])
  const [pendingCount, setPendingCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (simple) {
      setLoading(false)
      return
    }
    const today = parseYmd(todayYmd())
    const from = ymd(today)
    const to = ymd(addDays(today, 27))
    const calls: Promise<unknown>[] = [
      api.calendar(from, to).then((r) => setDays(r.days)),
      api
        .bookings(from, to, 'mine')
        .then((r) =>
          setMine(
            r.bookings
              .filter((b) => b.status === 'pending' || b.status === 'approved')
              .sort((a, b) => (a.date + a.hour).localeCompare(b.date + b.hour)),
          ),
        ),
    ]
    if (hasRole('supervisor')) {
      calls.push(
        api
          .bookings(ymd(addDays(today, -14)), ymd(addDays(today, 180)), 'all')
          .then((r) => setPendingCount(r.bookings.filter((b) => b.status === 'pending').length)),
      )
    }
    Promise.all(calls).finally(() => setLoading(false))
  }, [hasRole, simple])

  const next7 = useMemo(() => {
    const limit = ymd(addDays(parseYmd(todayYmd()), 7))
    return days.filter((d) => d.date >= todayYmd() && d.date < limit)
  }, [days])

  const stats = useMemo(() => {
    const open = next7.filter((d) => d.open)
    return {
      open: open.length,
      green: open.filter((d) => d.rag === 'green').length,
      need: open.filter((d) => d.rag === 'red' || d.rag === 'amber').length,
    }
  }, [next7])

  const needDays = useMemo(
    () => days.filter((d) => d.rag === 'red' || d.rag === 'amber').slice(0, 6),
    [days],
  )

  if (simple) return <SimpleHome />

  if (loading) {
    return (
      <div className="full-loader" style={{ minHeight: 320 }}>
        <Spinner large />
      </div>
    )
  }

  return (
    <div>
      <div className="page-head">
        <h1>{t('dash.title')}</h1>
        <p>{user?.name ? t('dash.subtitleName', { name: user.name.split(' ')[0] }) : t('dash.subtitle')}</p>
      </div>

      <div className={`grid ${hasRole('supervisor') ? 'cols-4' : 'cols-3'}`}>
        <div className="card stat">
          <span className="num">{stats.open}</span>
          <span className="lbl">{t('dash.openDays')}</span>
        </div>
        <div className="card stat">
          <span className="num" style={{ color: 'var(--green)' }}>{stats.green}</span>
          <span className="lbl">{t('dash.fullyCovered')}</span>
        </div>
        <div className="card stat">
          <span className="num" style={{ color: 'var(--red)' }}>{stats.need}</span>
          <span className="lbl">{t('dash.needPeople')}</span>
        </div>
        {hasRole('supervisor') && (
          <Link to="/approvals" className="card stat" style={{ textDecoration: 'none' }}>
            <span className="num" style={{ color: 'var(--amber)' }}>{pendingCount}</span>
            <span className="lbl">{t('dash.awaitingApproval')}</span>
          </Link>
        )}
      </div>

      <div className="grid cols-2 mt-24">
        <div className="card">
          <div className="card-head">
            <h3>{t('dash.daysNeedPeople')}</h3>
            <Link to="/calendar" className="btn btn-subtle btn-sm">
              <IconCalendar width={16} height={16} /> {t('dash.openCalendar')}
            </Link>
          </div>
          {needDays.length === 0 ? (
            <Empty title={t('dash.allCovered')}>
              <p className="muted">{t('dash.allCoveredHint')}</p>
            </Empty>
          ) : (
            <div className="list">
              {needDays.map((d) => {
                const meta = RAG_META[d.rag]
                return (
                  <Link to="/calendar" key={d.date} className="list-row" style={{ textDecoration: 'none' }}>
                    <RagDot rag={d.rag} />
                    <div className="grow">
                      <div className="title">{formatLong(d.date)}</div>
                      <div className="sub">
                        {t('dash.personHours', { covered: d.covered, required: d.required })}
                        {d.pending ? ` · ${t('cal.pending', { n: d.pending })}` : ''}
                      </div>
                    </div>
                    <div style={{ width: 120 }}>
                      <CoverageBar rag={d.rag} pct={Math.round(d.coverage * 100)} />
                    </div>
                    <span className="badge" style={{ background: meta.soft, color: meta.ink }}>
                      {Math.round(d.coverage * 100)}%
                    </span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h3>{t('dash.upcomingShifts')}</h3>
            <Link to="/my-shifts" className="btn btn-subtle btn-sm">
              <IconInbox width={16} height={16} /> {t('dash.allShifts')}
            </Link>
          </div>
          {mine.length === 0 ? (
            <Empty title={t('dash.noShiftsYet')}>
              <p className="muted">{t('dash.noShiftsHint')}</p>
              <Link to="/calendar" className="btn btn-primary mt-16">
                {t('topbar.bookShift')}
              </Link>
            </Empty>
          ) : (
            <div className="list">
              {mine.slice(0, 6).map((b) => (
                <div className="list-row" key={b.id}>
                  <div className="grow">
                    <div className="title">{formatLong(b.date)}</div>
                    <div className="sub">{formatHour(b.hour)}</div>
                  </div>
                  <span className={`badge ${b.status === 'approved' ? 'green' : 'amber'}`}>
                    {b.status === 'approved' ? t('status.approved') : t('status.pending')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
